import { ApiError, readLang, writeLang } from '../api/client.ts';
import { OrderStatusGroup } from '../core/order-status.ts';
import { createStore, useStore } from '../core/store.ts';
import { LANGS, STRINGS, type Lang, type Strings } from './strings.ts';

export { LANGS, type Lang, type Strings } from './strings.ts';

export const LANG_TITLES: Record<Lang, string> = { sr: 'Srpski', en: 'English', ru: 'Русский' };

const LOCALES: Record<Lang, string> = { sr: 'sr-Latn-RS', en: 'en-GB', ru: 'ru-RU' };

function isLang(value: string | null | undefined): value is Lang {
  return (LANGS as readonly string[]).includes(value ?? '');
}

/** Язык браузера, когда мы на нём говорим. */
export function deviceLang(): Lang | null {
  const code = navigator.language.slice(0, 2).toLowerCase();
  return isLang(code) ? code : null;
}

function initialLang(): Lang {
  const stored = readLang();
  // Язык браузера, если он нам знаком; иначе сербский: сервис работает в Белграде.
  return isLang(stored) ? stored : (deviceLang() ?? 'sr');
}

const langStore = createStore<Lang>(initialLang());

export function currentLang(): Lang {
  return langStore.get();
}

const langListeners = new Set<() => void>();

/** Кому язык нужен вне дерева React: service worker читает его из кеша. */
export function onLangChange(listener: () => void): void {
  langListeners.add(listener);
}

export function setLang(lang: Lang): void {
  writeLang(lang);
  langStore.set(lang);
  document.documentElement.lang = lang;
  for (const listener of langListeners) listener();
}

export function useLang(): Lang {
  return useStore(langStore);
}

export function useStrings(): Strings {
  return STRINGS[useStore(langStore)];
}

export function currentStrings(): Strings {
  return STRINGS[langStore.get()];
}

/** Текст ошибки для человека. Неизвестный код не выдумывается: показывается общий текст. */
export function errorText(strings: Strings, error: unknown): string {
  if (error instanceof ApiError) return strings.errors[error.code] ?? strings.somethingWentWrong;
  return strings.somethingWentWrong;
}

export function statusText(strings: Strings, code: string): string {
  return strings.statuses[code] ?? code;
}

export function shortStatusText(strings: Strings, code: string): string {
  return strings.statusesShort[code] ?? statusText(strings, code);
}

type Formatters = { day: Intl.DateTimeFormat; time: Intl.DateTimeFormat; dayTime: Intl.DateTimeFormat; longDay: Intl.DateTimeFormat };

const formatters = new Map<Lang, Formatters>();

function formattersFor(lang: Lang): Formatters {
  let found = formatters.get(lang);
  if (!found) {
    const locale = LOCALES[lang];
    found = {
      day: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }),
      time: new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }),
      dayTime: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }),
      longDay: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
    };
    formatters.set(lang, found);
  }
  return found;
}

const TODAY: Record<Lang, string> = { sr: 'Danas', en: 'Today', ru: 'Сегодня' };
const YESTERDAY: Record<Lang, string> = { sr: 'Juče', en: 'Yesterday', ru: 'Вчера' };
const JUST_NOW: Record<Lang, string> = { sr: 'upravo sada', en: 'just now', ru: 'только что' };

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** «14:35» в локали интерфейса. */
export function clockTime(lang: Lang, iso: string | Date): string {
  return formattersFor(lang).time.format(new Date(iso));
}

/** «Сегодня», «вчера» или дата: подпись прошлого заказа. */
export function relativeDay(lang: Lang, iso: string | Date): string {
  const date = new Date(iso);
  const now = new Date();
  if (sameDay(date, now)) return TODAY[lang];
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return YESTERDAY[lang];
  return formattersFor(lang).day.format(date);
}

/**
 * Момент оформления как имя заказа для клиента: «Danas, 12:40», «Juče, 18:05», «6. jun, 12:40».
 * Номер заказа клиент не видит.
 */
export function orderMoment(lang: Lang, iso: string | Date): string {
  return `${relativeDay(lang, iso)}, ${clockTime(lang, iso)}`;
}

/** «5 min назад», «вчера, 18:40»: время уведомления. */
export function relativeTime(lang: Lang, iso: string | Date): string {
  const date = new Date(iso);
  const seconds = (Date.now() - date.getTime()) / 1000;
  if (seconds < 60) return JUST_NOW[lang];
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return lang === 'sr' ? `pre ${minutes} min` : lang === 'ru' ? `${minutes} мин назад` : `${minutes} min ago`;
  }
  const format = formattersFor(lang);
  return sameDay(date, new Date()) ? format.time.format(date) : format.dayTime.format(date);
}

/** Дата `YYYY-MM-DD` словами: последний день акции, дата рождения. */
export function longDay(lang: Lang, isoDay: string): string {
  const [year, month, day] = isoDay.split('-').map(Number);
  if (!year || !month || !day) return isoDay;
  return formattersFor(lang).longDay.format(new Date(year, month - 1, day));
}

/**
 * Подпись заказа в работе: обещанное время или «скоро», если оно уже прошло.
 * null у завершённого заказа и заказа без обещания.
 */
export function arrivalText(lang: Lang, strings: Strings, status: string, expectedAt: string | null): string | null {
  if (!expectedAt || !OrderStatusGroup.isActive(status) || OrderStatusGroup.awaitingPayment.has(status)) return null;
  if (new Date(expectedAt).getTime() < Date.now()) return strings.arrivesSoon;
  return strings.arrivesAround(clockTime(lang, expectedAt));
}

/** Название позиции заказа на языке интерфейса; без перевода показывается сербское. */
export function localized(lang: Lang, sr: string, en: string | null | undefined, ru: string | null | undefined): string {
  if (lang === 'en' && en) return en;
  if (lang === 'ru' && ru) return ru;
  return sr;
}
